import { DotLegalAlert, DotLegalFullScreenSpinner, DotLegalSelect } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { Box } from "@mui/material";
import CvrSelector from "../cvrSelector/CvrSelector";
import CvrSelectorResult from "../cvrSelector/CvrSelectorResult";
import PaneLayout from "../../main/panes/paneLayout/PaneLayout";
import { definePane } from "../../main/panes/definePane";
import TemplateStepFields from "../../entityTemplates/templateStepFields/TemplateStepFields.tsx";
import { useAddLegalEntityPane } from "./AddLegalEntityPane.hooks";
import { AddLegalEntityPaneProps } from "./AddLegalEntityPane.types";
import LegalEntityTemplateField from "./LegalEntityTemplateField.tsx";

function AddLegalEntityPane(props: AddLegalEntityPaneProps) {
    const { translateString } = useTranslation();
    const {
        saveModel,
        onSaveModelChange,
        onSaveClick,
        validation,
        isSaving,
        onCvrLegalEntitySelected,
        onCvrLegalEntityRemoved,
        selectedCvrResult,
        templates,
        templatesIsLoading,
        isLoadingTemplate,
        onTemplateChange,
        selectedTemplateMissingAccess,
        fieldGroups,
        selectedTagIds,
        onTagsChange,
        editedLegalEntity,
        disableFields,
        onRequiredFieldChange,
        requiredFieldValidationErrors,
    } = useAddLegalEntityPane(props);

    return (
        <PaneLayout
            reserveFieldTooltipSpace
            header={translateString("createLegalEntity")}
            okButtonText={translateString("create")}
            onOkClick={onSaveClick}
            inProgress={isSaving}
            okButtonDisabled={selectedTemplateMissingAccess}
        >
            <DotLegalSelect
                options={templates}
                onChange={onTemplateChange}
                label={translateString("legalEntityTemplate")}
                noOptionsLabel={translateString("noOptions")}
                isLoading={templatesIsLoading}
                toolTipText={translateString("addLegalEntityTemplateTooltip")}
                disableClearable
                selectedItem={saveModel.entityTemplateId === "" ? null : saveModel.entityTemplateId}
                errorText={validation?.getErrorsForDisplay("entityTemplateId")}
            />

            {selectedTemplateMissingAccess && (
                <Box sx={{ marginTop: 2 }}>
                    <DotLegalAlert severity="info" warningText={translateString("missingRequiredAtCreationFieldAccess")} />
                </Box>
            )}

            {isLoadingTemplate && <DotLegalFullScreenSpinner />}

            {!selectedTemplateMissingAccess && (
                <TemplateStepFields
                    groups={fieldGroups}
                    renderField={(field) => (
                        <LegalEntityTemplateField
                            field={field}
                            saveModel={saveModel}
                            onNameChange={onSaveModelChange.onNameChange}
                            onCountryChange={onSaveModelChange.onCountryChange}
                            nameErrorText={validation?.getErrorsForDisplay("name")}
                            countryErrorText={validation?.getErrorsForDisplay("countryCode")}
                            disableSaveModelFields={disableFields}
                            legalEntity={editedLegalEntity}
                            onChange={onRequiredFieldChange}
                            validationErrors={requiredFieldValidationErrors}
                            selectedTagIds={selectedTagIds}
                            onTagsChange={onTagsChange}
                        />
                    )}
                />
            )}

            <CvrSelector onSelectLegalEntity={onCvrLegalEntitySelected} onLegalEntityRemoved={onCvrLegalEntityRemoved} />
            <Box sx={{ mt: 2 }}>
                <CvrSelectorResult clearSelectedResult={onCvrLegalEntityRemoved} selectedResult={selectedCvrResult} />
            </Box>
        </PaneLayout>
    );
}

export default definePane({ slot: "right", component: AddLegalEntityPane });
